import { type Menu, type MenuItemProps, message } from "antd";
import memoizeOne from "memoize-one";
import { type ComponentProps, forwardRef, useMemo, useRef } from "react";

import { BTDropdown } from "@btnet/core-components";

import {
    convertImageBlobToBase64String,
    isSupportedFileExtension,
    validateBase64Image,
} from "utilities/file/file";

import { BTIconImageOutlined } from "commonComponents/btWrappers/BTIcon";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import {
    isSelectionExclusivelyInTitle,
    supportedEditorImageTypes,
} from "commonComponents/btWrappers/editor/core/editor.utils";
import { type ICollapsableControl } from "commonComponents/btWrappers/editor/core/EditorControlBar/common/CollapsableControlGroup/CollapsableControlGroup";
import { EditorControlButton } from "commonComponents/btWrappers/editor/core/EditorControlBar/common/EditorControlButton/EditorControlButton";
import { InputRecaptureHackMenuItem } from "commonComponents/btWrappers/editor/core/EditorControlBar/common/InputRecaptureHack/InputRecaptureHack";
import ImageExtension from "commonComponents/btWrappers/editor/core/extensions/custom/image/ImageExtension";
import { findExtension } from "commonComponents/btWrappers/editor/core/extensions/editorExtensionUtils";
import { type IEditor } from "commonComponents/btWrappers/editor/core/useBTEditor.types";

const controlFriendlyName = "Image";
const browseComputerFriendlyName = "Browse";
const browseBuildertrendFilesFriendlyName = "Browse Buildertrend Files";
const controlIcon = <BTIconImageOutlined />;

const getImageExtensionMemo = memoizeOne((editor: IEditor) => {
    return findExtension(editor.tiptapEditor, ImageExtension);
});

const isBrowseBuildertrendFilesSupported = (editor: IEditor): boolean => {
    const imageExtension = getImageExtensionMemo(editor);

    const isImageInsertFromBtEnabled =
        imageExtension?.options.browseBuildertrendFilesConfig?.canViewPhotosPermission;
    return !!isImageInsertFromBtEnabled;
};

const isFileUploadSupported = (editor: IEditor): boolean => {
    const imageExtension = getImageExtensionMemo(editor);

    if (!imageExtension) {
        return false;
    }

    const options = imageExtension.options;
    return options.allowBase64 || !!options.uploadConfig;
};

interface IBrowseComputerInputProps {
    editor: IEditor;
}

/**
 * Why does this component exist? Because "hidden <input type="file" />" elements are the only cross-browser
 * way you can trigger file uploads programatically at this time :(. This component handles inserting a file
 * from your computer to the editor. In order to "activate" it though, you need to add a ref here and click on the
 * ref to open the file dialog.
 */
const BrowseComputerInput = forwardRef<HTMLInputElement, IBrowseComputerInputProps>(
    ({ editor }, forwardRef) => {
        const inputRef = useRef<HTMLInputElement | null>(null);
        const imageExtension = useMemo(
            () => findExtension(editor.tiptapEditor, ImageExtension),
            [editor],
        );

        if (!imageExtension) {
            throw new Error("Image extension not found");
        }

        const handleLocalFileSelected = async () => {
            if (
                !inputRef.current ||
                !inputRef.current.files ||
                inputRef.current.files.length !== 1
            ) {
                return;
            }

            const file = inputRef.current.files[0];
            if (
                !isSupportedFileExtension(
                    file.name,
                    Array.from(supportedEditorImageTypes.extensions),
                )
            ) {
                void message.error(
                    `The image type selected is not supported. Images with the following extensions are allowed: ${Array.from(
                        supportedEditorImageTypes.extensions,
                    ).join(", ")}`,
                    5,
                );
                return;
            }

            try {
                if (imageExtension.options.uploadConfig) {
                    const blobUrl = URL.createObjectURL(file);
                    editor.tiptapEditor
                        .chain()
                        .focus()
                        .setImage({
                            src: blobUrl,
                        })
                        .run();
                } else if (imageExtension.options.allowBase64) {
                    const base64String = await convertImageBlobToBase64String(file);
                    await validateBase64Image(base64String);
                    editor.tiptapEditor
                        .chain()
                        .focus()
                        .setImage({
                            src: base64String,
                        })
                        .run();
                }
            } catch (e) {
                void message.error(
                    "Image could not be inserted. Please try again or contact support.",
                );
            }
        };

        if (imageExtension.options.uploadConfig?.isImageUploadDisabledByBrowser) {
            return (
                // eslint-disable-next-line react/forbid-elements
                <input
                    ref={(ref) => {
                        inputRef.current = ref;
                        if (typeof forwardRef === "function") {
                            forwardRef(ref);
                        } else if (forwardRef) {
                            forwardRef.current = ref;
                        }
                    }}
                    type="button"
                    data-testid="hiddenFileUploadTrigger"
                    aria-hidden="true"
                    style={{ display: "none" }}
                    onClick={() =>
                        void message.warning(
                            "Image uploading unsupported on mobile at this time, feature available soon.",
                        )
                    }
                />
            );
        }

        return (
            // eslint-disable-next-line react/forbid-elements
            <input
                ref={(ref) => {
                    inputRef.current = ref;
                    if (typeof forwardRef === "function") {
                        forwardRef(ref);
                    } else if (forwardRef) {
                        forwardRef.current = ref;
                    }
                }}
                // Important: If this isn't here, images are persisted when selecting files again, causing the second image insert to fail
                // Can't create tests for this in jsdom because it's browser behavior
                value={[]}
                type="file"
                data-testid="hiddenFileUploadTrigger"
                aria-hidden="true"
                style={{ display: "none" }}
                // .tif,.tiff's are not able to be uploaded (even though they are accpeted by BT photos)
                // becase most browsers don't render base64 encoded versions
                accept={Array.from(supportedEditorImageTypes.extensions).join(",")}
                onChange={handleLocalFileSelected}
            />
        );
    },
);

interface IImageControlMenuItemProps extends ComponentProps<typeof Menu.Item> {
    editor: IEditor;
    controlObject: ICollapsableControl;
}

const ImageControlMenuItem = ({
    editor,
    controlObject,
    // Ant design does funky stuff to "understand" the menu heirachy. Need to pass these props down
    // and spread everything to prevent issues highlighting menu items, etc.
    ...menuItemProps
}: IImageControlMenuItemProps & MenuItemProps) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const showBrowseBuildertrendFiles = isBrowseBuildertrendFilesSupported(editor);
    if (showBrowseBuildertrendFiles) {
        return (
            <InputRecaptureHackMenuItem
                menuComponent={BTMenu.SubMenu}
                editor={editor}
                menuProps={{
                    icon: controlIcon,
                    title: controlFriendlyName,
                    disabled: !controlObject.isControlEnabled(editor),
                    className: "btEditorControlBarMenuItem",
                    testid: "imageCollapsedMenuItem",
                }}
                icon={controlIcon}
                key={controlObject.key}
                {...menuItemProps}
            >
                <InputRecaptureHackMenuItem
                    menuComponent={BTMenu.Item}
                    editor={editor}
                    menuProps={{
                        testid: "browseComputerFiles",
                        onClick: () => {
                            if (fileInputRef.current) {
                                fileInputRef.current.click();
                            }
                        },
                        className: "btEditorControlBarMenuItem",
                    }}
                    key="browseComputerFiles"
                >
                    {browseComputerFriendlyName}
                    <BrowseComputerInput ref={fileInputRef} editor={editor} />
                </InputRecaptureHackMenuItem>
                <InputRecaptureHackMenuItem
                    menuComponent={BTMenu.Item}
                    editor={editor}
                    menuProps={{
                        testid: "browseBuildertrendFiles",
                        onClick: () => {
                            editor.editorStateDispatch({
                                type: "setShowBrowseImagesOverlay",
                                payload: true,
                            });
                        },
                        className: "btEditorControlBarMenuItem",
                    }}
                    key="browseBuildertrendFiles"
                >
                    {browseBuildertrendFilesFriendlyName}
                </InputRecaptureHackMenuItem>
            </InputRecaptureHackMenuItem>
        );
    } else {
        return (
            <InputRecaptureHackMenuItem
                menuComponent={BTMenu.Item}
                editor={editor}
                {...menuItemProps}
                menuProps={{
                    testid: controlObject.key,
                    icon: controlIcon,
                    onClick: () => {
                        if (fileInputRef.current) {
                            fileInputRef.current.click();
                        }
                    },
                }}
                key={controlObject.key}
            >
                {controlFriendlyName}
                <BrowseComputerInput ref={fileInputRef} editor={editor} />
            </InputRecaptureHackMenuItem>
        );
    }
};

interface IImageControlButtonProps {
    editor: IEditor;
    supportedExtensions: Set<string>;
    controlObject: ICollapsableControl;
}

const ImageControlButton = ({ editor, controlObject }: IImageControlButtonProps) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const showBrowseBuildertrendFiles = isBrowseBuildertrendFilesSupported(editor);
    const isImageToggled = editor.tiptapEditor.isActive(controlObject.key);

    if (showBrowseBuildertrendFiles) {
        return (
            <BTDropdown
                testid="image-control-dropdown"
                overlay={
                    <BTMenu testid="imageControlMenu">
                        <InputRecaptureHackMenuItem
                            editor={editor}
                            menuComponent={BTMenu.Item}
                            menuProps={{
                                testid: "browseComputerFiles",
                                onClick: () => {
                                    if (fileInputRef.current) {
                                        fileInputRef.current.click();
                                    }
                                },
                                className: "btEditorControlBarMenuItem",
                            }}
                            key="browseComputerFiles"
                        >
                            {browseComputerFriendlyName}
                            <BrowseComputerInput ref={fileInputRef} editor={editor} />
                        </InputRecaptureHackMenuItem>
                        <InputRecaptureHackMenuItem
                            editor={editor}
                            menuComponent={BTMenu.Item}
                            menuProps={{
                                testid: "browseBuildertrendFiles",
                                onClick: () => {
                                    editor.editorStateDispatch({
                                        type: "setShowBrowseImagesOverlay",
                                        payload: true,
                                    });
                                },
                                className: "btEditorControlBarMenuItem",
                            }}
                            key="browseBuildertrendFiles"
                        >
                            {browseBuildertrendFilesFriendlyName}
                        </InputRecaptureHackMenuItem>
                    </BTMenu>
                }
                disabled={!controlObject.isControlEnabled(editor)}
            >
                <EditorControlButton
                    friendlyName={controlFriendlyName}
                    data-testid="imageControl"
                    icon={controlIcon}
                    isToggled={isImageToggled}
                    disabled={!controlObject.isControlEnabled(editor)}
                    dropdown
                />
            </BTDropdown>
        );
    } else {
        return (
            <>
                <EditorControlButton
                    friendlyName={controlFriendlyName}
                    data-testid="imageControl"
                    icon={controlIcon}
                    isToggled={isImageToggled}
                    disabled={!controlObject.isControlEnabled(editor)}
                    onClick={() => {
                        if (fileInputRef.current) {
                            fileInputRef.current.click();
                        }
                    }}
                />
                <BrowseComputerInput ref={fileInputRef} editor={editor} />
            </>
        );
    }
};

const extensionId = "image";
export const imageControl: ICollapsableControl = {
    key: extensionId,
    isControlSupported(_, editor) {
        return isFileUploadSupported(editor);
    },
    isControlEnabled({ tiptapEditor }) {
        return !isSelectionExclusivelyInTitle(tiptapEditor, tiptapEditor.state.selection);
    },
    expandedElement(editor, supportedExtensions) {
        return (
            <ImageControlButton
                editor={editor}
                controlObject={this}
                supportedExtensions={supportedExtensions}
            />
        );
    },
    collapsedActiveKeys({ tiptapEditor }) {
        return tiptapEditor.isActive(extensionId) ? [extensionId] : [];
    },
    collapsedMenuItem(editor) {
        return (
            <InputRecaptureHackMenuItem
                menuComponent={ImageControlMenuItem}
                key={this.key}
                editor={editor}
                menuProps={{
                    controlObject: this,
                    editor: editor,
                }}
            />
        );
    },
};
export default imageControl;
